import React, { useEffect } from 'react';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { appWithTranslation } from 'next-i18next/pages';
import { Provider } from 'react-redux';

import { handleSDKKey } from '@highlight-cp/feature-flags';
import { createLoggers } from '@highlight-cp/logger';
import { setupGlobalClientErrorHandlers } from '@highlight-cp/metrics/client';
import { initAmplitude } from '@highlight-cp/user-analytics';
import { ImpersonationSessionBridge } from 'app/auth/ImpersonationSessionBridge';
import { AuthGuard } from 'app/components/AuthGuard';
import ErrorBoundary from 'app/components/ErrorBoundary';
import MetaInfo from 'app/components/MetaInfo';
import PageViewTracker from 'app/components/PageViewTracker';
import ThemeShell from 'app/components/ThemeShell';
import { ClientPortalUserProvider } from 'features/user/useSessionUser';
import { reduxWrapper } from 'store';
import {
  ClientPortalAuthClientProvider,
  ClientPortalAuthProvider,
} from '../auth/ClientPortalAuthProvider';

import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/dropzone/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
import '@mantine/tiptap/styles.layer.css';
import 'mantine-datatable/styles.layer.css';
import 'app/styles/variables.css';

import { AppTypeProvider, ProjectIdProvider } from '@highlight-cp/redux-state';
import type { ServerSideProps } from 'app/getServerSideProps';

const { debug, error } = createLoggers({ tag: 'App' });

// Built statically, so they have no Heimdall runtime config.
const ERROR_PAGE_PATHS = ['/404', '/500', '/_error'];

const Layout = dynamic(() => import('app/components/Layout'));

const ConfigCatProvider = dynamic(
  () => import('configcat-react').then((mod) => mod.ConfigCatProvider),
  {
    ssr: false,
  },
);

// development env
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  /* eslint-disable  */
  const ReactDOM = require('react-dom');

  const axe = require('@axe-core/react');
  axe(React, ReactDOM, 1000);
}

function _App({ Component, ...rest }: AppProps<ServerSideProps>) {
  const { store, props } = reduxWrapper.useWrappedStore(rest);
  const heimdallBaseUrl = props.pageProps.heimdallBaseUrl;
  const hasHeimdallBaseUrl = typeof heimdallBaseUrl === 'string' && heimdallBaseUrl.trim() !== '';
  const configCatSdkKey = handleSDKKey(rest.pageProps.configCatSdkKey);

  const isGpcOptOut =
    typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true;

  // Set an explicit opt-out flag so analytics calls made immediately after mount are
  // safely suppressed (the Analytics.track path in analytics.ts also reads this), at
  // parity with the highlighter app.
  if (typeof window !== 'undefined') {
    (window as any).__HIGHLIGHT_GPC_OPTOUT__ = isGpcOptOut;
  }

  useEffect(() => {
    if (isGpcOptOut) {
      return;
    }

    if (typeof window !== 'undefined' && rest.pageProps.amplitudeSdk) {
      // Page views are tracked manually via <PageViewTracker />; the built-in capture
      // is broken here (0 events in 90 days) and conflicts with autocapture: false.
      initAmplitude({ sdkKey: rest.pageProps.amplitudeSdk, pageViews: false });
    }
  }, [rest.pageProps.amplitudeSdk, isGpcOptOut]);

  useEffect(() => {
    return setupGlobalClientErrorHandlers();
  }, []);

  // Error pages get fonts and theme only: no auth, store or app shell that could fail too.
  if (ERROR_PAGE_PATHS.includes(rest.router.pathname)) {
    return (
      <ErrorBoundary>
        <ThemeShell>
          <MetaInfo />
          <Component {...props.pageProps} />
        </ThemeShell>
      </ErrorBoundary>
    );
  }

  if (!hasHeimdallBaseUrl) {
    throw new Error('heimdallBaseUrl is required for Client Portal auth runtime');
  }

  return (
    <ErrorBoundary>
      <ConfigCatProvider
        sdkKey={configCatSdkKey}
        options={{
          setupHooks: (hooks) => {
            hooks.on('clientReady', () => {
              debug('ConfigCat Client ready');
            });

            hooks.on('clientError', (err) => {
              error('ConfigCat Client error', { err });
            });
          },
        }}
      >
        {/* Above the auth providers: the Client Portal session lives in the store. */}
        <Provider store={store}>
          <ClientPortalAuthClientProvider heimdallBaseUrl={heimdallBaseUrl}>
            <ClientPortalAuthProvider>
              <ImpersonationSessionBridge>
                <ClientPortalUserProvider>
                  <PageViewTracker />
                  <AppTypeProvider.Client>
                    <ProjectIdProvider>
                      <Layout>
                        <MetaInfo />
                        <AuthGuard>
                          <Component {...props.pageProps} />
                        </AuthGuard>
                      </Layout>
                    </ProjectIdProvider>
                  </AppTypeProvider.Client>
                </ClientPortalUserProvider>
              </ImpersonationSessionBridge>
            </ClientPortalAuthProvider>
          </ClientPortalAuthClientProvider>
        </Provider>
      </ConfigCatProvider>
    </ErrorBoundary>
  );
}

export default appWithTranslation(_App as any);
